import { useRef, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { curatorProfileApi } from '@/api/users';
import { uploadsApi } from '@/api/uploads';
import { useAuthStore } from '@/stores/auth';
import { humanizeError } from '@/lib/errors';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle } from '@/components/ui/Card';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useShellContext } from '@/components/layout/shellContext';

export function CuratorProfilePage() {
  const { t } = useTranslation();
  // Выход — тот же, что в оболочке (одно место выхода, app/staffLogout.test.ts). Вне оболочки контекста нет.
  const shell = useShellContext() as ReturnType<typeof useShellContext> | undefined;
  const authUser = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const fileRef = useRef<HTMLInputElement>(null);

  const profile = useQuery({
    queryKey: ['curator', 'profile', 'me'],
    queryFn: () => curatorProfileApi.me(),
  });

  // Editable identity fields. Initialised lazily from the query once it lands.
  const [fullName, setFullName] = useState<string | null>(null);
  const [phone, setPhone] = useState<string | null>(null);
  const [specialization, setSpecialization] = useState<string | null>(null);
  const [bio, setBio] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [avatarErr, setAvatarErr] = useState<string | null>(null);

  const p = profile.data;
  const fullNameValue = fullName ?? p?.full_name ?? '';
  const phoneValue = phone ?? p?.phone ?? '';
  const specializationValue = specialization ?? p?.specialization ?? '';
  const bioValue = bio ?? p?.bio ?? '';

  const save = useMutation({
    mutationFn: () =>
      curatorProfileApi.update({
        full_name: fullNameValue,
        phone: phoneValue,
        specialization: specializationValue,
        bio: bioValue,
      }),
    onSuccess: (updated) => {
      setSaveErr(null);
      setSavedMsg(t('curatorProfile.saved'));
      profile.refetch();
      // Sync the auth store so the header / sidebar reflect the new name.
      if (authUser) setUser({ ...authUser, full_name: updated.full_name });
    },
    onError: (e: unknown) => {
      setSavedMsg(null);
      setSaveErr(humanizeError(e));
    },
  });

  const uploadAvatar = useMutation({
    mutationFn: async (file: File) => {
      const res = await uploadsApi.upload(file, 'avatars');
      return curatorProfileApi.update({ avatar_url: res.url });
    },
    onSuccess: (updated) => {
      setAvatarErr(null);
      profile.refetch();
      if (authUser) setUser({ ...authUser, avatar_url: updated.avatar_url });
    },
    onError: (e: unknown) => setAvatarErr(humanizeError(e)),
  });

  // ----- password change -----
  const [oldPass, setOldPass] = useState('');
  const [newPass, setNewPass] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [pwdErr, setPwdErr] = useState<string | null>(null);

  const changePassword = useMutation({
    mutationFn: () =>
      curatorProfileApi.changePassword({ old_password: oldPass, new_password: newPass }),
    onSuccess: () => {
      setPwdErr(null);
      setOldPass('');
      setNewPass('');
      setConfirmPass('');
    },
    onError: (e: unknown) => setPwdErr(humanizeError(e)),
  });

  const submitPassword = () => {
    if (newPass.length < 8) {
      setPwdErr(t('curatorProfile.passwordTooShort'));
      return;
    }
    if (newPass !== confirmPass) {
      setPwdErr(t('curatorProfile.passwordMismatch'));
      return;
    }
    changePassword.mutate();
  };

  return (
    <>
      <PageHeader title={t('curatorProfile.title')} subtitle={p?.email ?? authUser?.email} />

      <Card className="mb-4 flex items-center gap-4">
        <Avatar name={p?.full_name ?? authUser?.full_name} src={p?.avatar_url} size="lg" />
        <div className="flex-1">
          <CardTitle>{p?.full_name ?? authUser?.full_name}</CardTitle>
          <p className="text-sm text-on-surface-variant">{p?.email ?? authUser?.email}</p>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => e.target.files?.[0] && uploadAvatar.mutate(e.target.files[0])}
        />
        <Button
          variant="secondary"
          size="sm"
          loading={uploadAvatar.isPending}
          onClick={() => fileRef.current?.click()}
        >
          <span className="material-symbols-outlined">photo_camera</span>
          {t('curatorProfile.changePhoto')}
        </Button>
        {avatarErr && <p className="w-full text-xs text-error">{avatarErr}</p>}
      </Card>

      {p && (
        <Card className="mb-4 grid grid-cols-1 gap-4 text-center sm:grid-cols-3">
          <div>
            <p className="text-2xl font-display font-semibold text-on-surface">{p.active_students}</p>
            <p className="text-xs text-on-surface-variant">{t('curatorProfile.activeStudents')}</p>
          </div>
          <div>
            <p className="text-2xl font-display font-semibold text-on-surface">{p.max_students || '—'}</p>
            <p className="text-xs text-on-surface-variant">{t('curatorProfile.studentsLimit')}</p>
          </div>
          <div>
            <p className="text-2xl font-display font-semibold text-on-surface">
              {p.rating ? p.rating.toFixed(1) : '—'}
            </p>
            <p className="text-xs text-on-surface-variant">{t('curatorProfile.rating')}</p>
          </div>
        </Card>
      )}

      <Card>
        <CardTitle>{t('curatorProfile.sectionMain')}</CardTitle>
        <div className="mt-4 grid gap-3">
          <Input
            label={t('curatorProfile.fullName')}
            value={fullNameValue}
            onChange={(e) => {
              setFullName(e.target.value);
              setSavedMsg(null);
            }}
          />
          <Input label={t('auth.email')} disabled value={p?.email ?? authUser?.email ?? ''} />
          <Input
            label={t('curatorProfile.phone')}
            placeholder="+7 700 …"
            value={phoneValue}
            onChange={(e) => {
              setPhone(e.target.value);
              setSavedMsg(null);
            }}
          />
          <Input
            label={t('curatorProfile.specialization')}
            placeholder={t('curatorProfile.specializationPlaceholder')}
            value={specializationValue}
            onChange={(e) => {
              setSpecialization(e.target.value);
              setSavedMsg(null);
            }}
          />
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-label text-on-surface-variant">
              {t('curatorProfile.bio')}
            </span>
            <textarea
              rows={4}
              placeholder={t('curatorProfile.bioPlaceholder')}
              value={bioValue}
              onChange={(e) => {
                setBio(e.target.value);
                setSavedMsg(null);
              }}
              className="w-full resize-none rounded-button bg-surface-container-lowest px-4 py-3 text-on-surface shadow-soft transition placeholder:text-on-surface-variant/60 focus:shadow-ambient focus:outline-none"
            />
          </label>
          {saveErr && <p className="text-xs text-error">{saveErr}</p>}
          <div className="flex items-center justify-end gap-3 pt-2">
            {savedMsg && <span className="text-xs text-success">✓ {savedMsg}</span>}
            <Button onClick={() => save.mutate()} loading={save.isPending}>
              {t('common.save')}
            </Button>
          </div>
        </div>
      </Card>

      <Card className="mt-4">
        <CardTitle>{t('curatorProfile.sectionPassword')}</CardTitle>
        <div className="mt-4 grid gap-3">
          <Input
            type="password"
            label={t('curatorProfile.currentPassword')}
            value={oldPass}
            onChange={(e) => setOldPass(e.target.value)}
          />
          <Input
            type="password"
            label={t('curatorProfile.newPassword')}
            hint={t('curatorProfile.passwordMinLength')}
            value={newPass}
            onChange={(e) => setNewPass(e.target.value)}
          />
          <Input
            type="password"
            label={t('curatorProfile.confirmNewPassword')}
            value={confirmPass}
            onChange={(e) => setConfirmPass(e.target.value)}
          />
          {pwdErr && <p className="text-xs text-error">{pwdErr}</p>}
          {changePassword.isSuccess && !pwdErr && (
            <p className="text-xs text-success">✓ {t('curatorProfile.passwordChanged')}</p>
          )}
          <div className="flex justify-end pt-2">
            <Button
              onClick={submitPassword}
              loading={changePassword.isPending}
              disabled={!oldPass || !newPass}
            >
              {t('curatorProfile.changePassword')}
            </Button>
          </div>
        </div>
      </Card>

      {/* «Выйти» последней строкой — и на телефоне, и на широком экране (13.10.2026, docs/CURATOR-MOBILE-DESIGN.md §2):
          прежде выход куратора был только в боковом меню. */}
      {shell && (
        <Card className="mt-4">
          <button
            type="button"
            onClick={() => void shell.logout()}
            className="flex min-h-11 w-full items-center justify-between text-left text-sm font-semibold text-on-surface"
          >
            <span>{t('common.logout')}</span>
            <span className="material-symbols-outlined text-xl text-on-surface-variant">logout</span>
          </button>
        </Card>
      )}
    </>
  );
}
